import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
import { Calendar as CalendarIcon, AlertTriangle, TrendingUp, Flag, Briefcase, GraduationCap, Heart, Leaf, Building, Wheat, Plane, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
type EventCategory = 'hari-besar' | 'agenda-presiden' | 'sidang-dpr' | 'kebijakan' | 'kunjungan' | 'momentum';
type CalendarEvent = {
  id: string;
  date: string; // YYYY-MM-DD
  title: string;
  category: EventCategory;
  risk: 'rendah' | 'sedang' | 'tinggi' | 'kritis';
  predictedIssues: string[];
  region?: string;
  icon: any;
};
const events: CalendarEvent[] = [{
  id: 'E-001',
  date: '2026-05-25',
  title: 'Hari Kebangkitan Nasional',
  category: 'hari-besar',
  risk: 'sedang',
  predictedIssues: ['Narasi nasionalisme vs kritik kebijakan', 'Refleksi 100 hari pemerintahan'],
  icon: Flag
}, {
  id: 'E-002',
  date: '2026-05-26',
  title: 'Kunjungan Presiden ke Riau',
  category: 'kunjungan',
  risk: 'tinggi',
  region: 'Riau',
  predictedIssues: ['Isu lahan & sawit', 'Karhutla', 'Investasi hilirisasi minyak'],
  icon: Plane
}, {
  id: 'E-003',
  date: '2026-05-27',
  title: 'Sidang Paripurna DPR — RAPBN-P',
  category: 'sidang-dpr',
  risk: 'tinggi',
  predictedIssues: ['Tarik ulur subsidi BBM', 'Anggaran MBG vs kritik fiskal', 'Debat alokasi pertahanan'],
  icon: Building
}, {
  id: 'E-004',
  date: '2026-05-29',
  title: 'Pengumuman Kebijakan Pangan',
  category: 'kebijakan',
  risk: 'kritis',
  predictedIssues: ['Reaksi petani vs konsumen', 'Hoaks harga & stok', 'Narasi swasembada vs realita lapangan'],
  icon: Wheat
}, {
  id: 'E-005',
  date: '2026-06-01',
  title: 'Hari Lahir Pancasila',
  category: 'hari-besar',
  risk: 'sedang',
  predictedIssues: ['Narasi ideologi vs identitas', 'Polemik intoleransi daerah'],
  icon: Flag
}, {
  id: 'E-006',
  date: '2026-06-05',
  title: 'Hari Lingkungan Hidup Sedunia',
  category: 'momentum',
  risk: 'sedang',
  predictedIssues: ['Kritik proyek IKN & deforestasi', 'Tambang nikel Sulawesi', 'Banjir Demak'],
  icon: Leaf
}, {
  id: 'E-007',
  date: '2026-06-12',
  title: 'Pengumuman Kenaikan UMP',
  category: 'kebijakan',
  risk: 'tinggi',
  predictedIssues: ['Demo buruh nasional', 'Reaksi pengusaha', 'Polemik formula UMP regional'],
  icon: Briefcase
}, {
  id: 'E-008',
  date: '2026-06-15',
  title: 'Kunjungan Presiden ke Papua',
  category: 'kunjungan',
  risk: 'kritis',
  region: 'Papua',
  predictedIssues: ['Keamanan & HAM', 'Infrastruktur tertinggal', 'Kebocoran jadwal sensitif'],
  icon: Plane
}, {
  id: 'E-009',
  date: '2026-06-21',
  title: 'Hari Bhayangkara',
  category: 'hari-besar',
  risk: 'rendah',
  predictedIssues: ['Apresiasi vs kritik kinerja Polri'],
  icon: Flag
}, {
  id: 'E-010',
  date: '2026-06-25',
  title: 'Pidato Kenegaraan Pra-HUT RI',
  category: 'agenda-presiden',
  risk: 'tinggi',
  predictedIssues: ['Analisis prestasi vs janji kampanye', 'Counter-narrative dari oposisi'],
  icon: TrendingUp
}, {
  id: 'E-011',
  date: '2026-07-01',
  title: 'Hari Kesehatan Nasional',
  category: 'momentum',
  risk: 'rendah',
  predictedIssues: ['Stunting & gizi buruk', 'Kekurangan dokter di Papua'],
  icon: Heart
}, {
  id: 'E-012',
  date: '2026-07-23',
  title: 'Hari Anak Nasional',
  category: 'hari-besar',
  risk: 'rendah',
  predictedIssues: ['Program MBG', 'Pendidikan inklusif'],
  icon: GraduationCap
}];
const categoryMeta: Record<EventCategory, {
  label: string;
  bg: string;
  border: string;
  text: string;
}> = {
  'hari-besar': {
    label: 'Hari Besar',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700'
  },
  'agenda-presiden': {
    label: 'Agenda Presiden',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
    text: 'text-primary'
  },
  'sidang-dpr': {
    label: 'Sidang DPR',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700'
  },
  kebijakan: {
    label: 'Kebijakan',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700'
  },
  kunjungan: {
    label: 'Kunjungan',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700'
  },
  momentum: {
    label: 'Momentum',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700'
  }
};
const riskTone: Record<string, string> = {
  rendah: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sedang: 'bg-amber-50 text-amber-700 border-amber-200',
  tinggi: 'bg-orange-50 text-orange-700 border-orange-200',
  kritis: 'bg-red-50 text-red-700 border-red-200'
};
const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const weekdays = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
function getMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells: Array<{
    date: Date | null;
  }> = [];
  for (let i = 0; i < startDay; i++) cells.push({
    date: null
  });
  for (let d = 1; d <= days; d++) cells.push({
    date: new Date(year, month, d)
  });
  while (cells.length % 7 !== 0) cells.push({
    date: null
  });
  return cells;
}
function fmtDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function NarrativeCalendar() {
  const [cursor, setCursor] = useState(new Date(2026, 4, 1)); // May 2026
  const [filter, setFilter] = useState<EventCategory | 'semua'>('semua');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const eventsByDate: Record<string, CalendarEvent[]> = {};
  for (const ev of events) {
    if (filter !== 'semua' && ev.category !== filter) continue;
    if (!eventsByDate[ev.date]) eventsByDate[ev.date] = [];
    eventsByDate[ev.date].push(ev);
  }
  const cells = getMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const monthEvents = events.filter(e => {
    const d = new Date(e.date);
    return d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth() && (filter === 'semua' || e.category === filter);
  });
  const criticalThisMonth = monthEvents.filter(e => e.risk === 'kritis').length;
  const highThisMonth = monthEvents.filter(e => e.risk === 'tinggi').length;
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-2529">
      <PageHeader group="Intelijen Presiden" title="National Narrative Calendar" description="Kalender prediktif isu nasional berdasarkan hari besar, agenda Presiden, sidang DPR, kebijakan, dan momentum publik." icon={CalendarIcon} data-id="element-2530" />

      {/* KPI summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-2531">
        <SummaryCard tone="primary" label="Total Event" value={`${monthEvents.length}`} sub={months[cursor.getMonth()]} data-id="element-2532" />
        <SummaryCard tone="red" label="Risiko Kritis" value={`${criticalThisMonth}`} sub="butuh prep khusus" data-id="element-2533" />
        <SummaryCard tone="amber" label="Risiko Tinggi" value={`${highThisMonth}`} sub="butuh monitoring" data-id="element-2534" />
        <SummaryCard tone="emerald" label="Kunjungan" value={`${monthEvents.filter(e => e.category === 'kunjungan').length}`} sub="agenda Presiden" data-id="element-2535" />
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-2 flex-wrap" data-id="element-2536">
        <button onClick={() => setFilter('semua')} className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded border transition ${filter === 'semua' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border/60 text-muted-foreground hover:bg-muted'}`} data-id="element-2537">
          Semua ({events.length})
        </button>
        {(Object.keys(categoryMeta) as EventCategory[]).map(c => {
        const m = categoryMeta[c];
        const count = events.filter(e => e.category === c).length;
        const active = filter === c;
        return <button key={c} onClick={() => setFilter(c)} className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded border transition flex items-center gap-1.5 ${active ? `${m.bg} ${m.border} ${m.text} font-bold` : 'bg-card border-border/60 text-muted-foreground hover:bg-muted'}`} data-id="element-2538">
              {m.label} ({count})
            </button>;
      })}
      </div>

      <div className="grid grid-cols-12 gap-4 sm:gap-6" data-id="element-2539">
        {/* Calendar */}
        <div className="col-span-12 lg:col-span-8" data-id="element-2540">
          <Card className="bg-card border-border/60" data-id="element-2541">
            <CardContent className="p-4 sm:p-5" data-id="element-2542">
              <div className="flex items-center justify-between mb-4" data-id="element-2543">
                <div data-id="element-2544">
                  <h3 className="font-heading text-lg font-bold tracking-tight" data-id="element-2545">
                    {months[cursor.getMonth()]} {cursor.getFullYear()}
                  </h3>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5" data-id="element-2546">
                    Prediksi isu — Auto-generated
                  </p>
                </div>
                <div className="flex items-center gap-1" data-id="element-2547">
                  <Button variant="outline" size="sm" className="px-2" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-id="element-2548">
                    <ChevronLeft className="size-3.5" data-id="element-2549" />
                  </Button>
                  <Button variant="outline" size="sm" className="px-3 font-mono text-[10px]" onClick={() => setCursor(new Date(2026, 4, 1))} data-id="element-2550">
                    Hari ini
                  </Button>
                  <Button variant="outline" size="sm" className="px-2" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-id="element-2551">
                    <ChevronRight className="size-3.5" data-id="element-2552" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1.5 mb-1.5" data-id="element-2553">
                {weekdays.map(w => <div key={w} className="text-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground py-1" data-id="element-2554">
                    {w}
                  </div>)}
              </div>
              <div className="grid grid-cols-7 gap-1.5" data-id="element-2555">
                {cells.map((c, i) => {
                if (!c.date) return <div key={i} className="aspect-square" data-id="element-2556" />;
                const key = fmtDate(c.date);
                const dayEvents = eventsByDate[key] ?? [];
                const isToday = key === '2026-05-23';
                return <div key={i} className={`aspect-square rounded-md border p-1.5 flex flex-col gap-0.5 overflow-hidden ${isToday ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : dayEvents.length > 0 ? 'border-border/60 bg-muted/20' : 'border-border/40 bg-card'}`} data-id="element-2557">
                      <div className="flex items-center justify-between" data-id="element-2558">
                        <span className={`font-mono text-[10px] font-bold ${isToday ? 'text-primary' : 'text-foreground'}`} data-id="element-2559">
                          {c.date.getDate()}
                        </span>
                        {dayEvents.length > 0 && <span className="font-mono text-[8px] text-muted-foreground" data-id="element-2560">
                            {dayEvents.length}
                          </span>}
                      </div>
                      <div className="flex-1 space-y-0.5 overflow-hidden" data-id="element-2561">
                        {dayEvents.slice(0, 2).map(ev => {
                      const meta = categoryMeta[ev.category];
                      return <div key={ev.id} className={`text-[9px] leading-tight px-1 py-0.5 rounded truncate ${meta.bg} ${meta.text} border ${meta.border}`} title={ev.title} data-id="element-2562">
                              {ev.title}
                            </div>;
                    })}
                        {dayEvents.length > 2 && <p className="font-mono text-[8px] text-muted-foreground px-1" data-id="element-2563">
                            +{dayEvents.length - 2} lagi
                          </p>}
                      </div>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming events list */}
        <div className="col-span-12 lg:col-span-4" data-id="element-2564">
          <Card className="bg-card border-border/60" data-id="element-2565">
            <CardContent className="p-0" data-id="element-2566">
              <div className="px-4 py-3 border-b border-border/60" data-id="element-2567">
                <h3 className="font-heading text-sm font-bold" data-id="element-2568">
                  Event Mendatang
                </h3>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5" data-id="element-2569">
                  {monthEvents.length} event · {months[cursor.getMonth()]}
                </p>
              </div>
              <div className="max-h-[480px] overflow-y-auto divide-y divide-border/60" data-id="element-2570">
                {monthEvents.sort((a, b) => a.date.localeCompare(b.date)).map(ev => {
                const meta = categoryMeta[ev.category];
                const d = new Date(ev.date);
                return <button key={ev.id} onClick={() => setSelectedEvent(ev)} className="w-full text-left p-3 hover:bg-muted/30 transition flex items-start gap-3 group" data-id="element-2571">
                        <div className={`size-10 rounded-md ${meta.bg} border ${meta.border} flex flex-col items-center justify-center shrink-0`} data-id="element-2572">
                          <span className={`font-mono text-[8px] uppercase ${meta.text}`} data-id="element-2573">
                            {months[d.getMonth()].slice(0, 3)}
                          </span>
                          <span className={`font-bold text-sm ${meta.text}`} data-id="element-2574">
                            {d.getDate()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2575">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5" data-id="element-2576">
                            <Badge variant="outline" className={`font-mono text-[9px] h-4 px-1.5 ${meta.bg} ${meta.text} ${meta.border}`} data-id="element-2577">
                              {meta.label}
                            </Badge>
                            <Badge variant="outline" className={`font-mono text-[9px] h-4 px-1.5 ${riskTone[ev.risk]}`} data-id="element-2578">
                              {ev.risk}
                            </Badge>
                          </div>
                          <p className="text-xs font-semibold leading-snug" data-id="element-2579">
                            {ev.title}
                          </p>
                          {ev.region && <p className="font-mono text-[10px] text-muted-foreground mt-0.5" data-id="element-2580">
                              📍 {ev.region}
                            </p>}
                          <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-relaxed" data-id="element-2581">
                            {ev.predictedIssues[0]}
                          </p>
                        </div>
                      </button>;
              })}
                {monthEvents.length === 0 && <div className="text-center py-12 px-4" data-id="element-2582">
                    <CalendarIcon className="size-8 text-muted-foreground/40 mx-auto mb-2" data-id="element-2583" />
                    <p className="text-sm text-muted-foreground" data-id="element-2584">
                      Tidak ada event di bulan ini
                    </p>
                  </div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Predictive insight */}
      <Card className="bg-card border-border/60 border-l-4 border-l-primary" data-id="element-2585">
        <CardContent className="p-4 sm:p-5" data-id="element-2586">
          <div className="flex items-start gap-3" data-id="element-2587">
            <div className="size-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-2588">
              <AlertTriangle className="size-5 text-primary" data-id="element-2589" />
            </div>
            <div className="flex-1" data-id="element-2590">
              <p className="font-mono text-[10px] uppercase tracking-wider text-primary font-bold" data-id="element-2591">
                AI Predictive Insight
              </p>
              <p className="text-sm text-foreground mt-1.5 leading-relaxed" data-id="element-2592">
                Bulan ini ada <b data-id="element-2593">{criticalThisMonth} event berisiko KRITIS</b>{' '}
                dan <b data-id="element-2594">{highThisMonth} event berisiko TINGGI</b>. Konsentrasi
                risiko tertinggi pada minggu 25–29 Mei (kunjungan Riau + Sidang
                DPR + Kebijakan Pangan). Disarankan menyiapkan{' '}
                <b data-id="element-2595">command center khusus</b> dan timeline narasi terpadu 7 hari
                sebelumnya.
              </p>
              <div className="flex gap-2 mt-3" data-id="element-2596">
                <Button size="sm" onClick={() => toast.success('Brief 7 hari dibuat oleh AI Analyst')} data-id="element-2597">
                  Generate Brief 7 Hari
                </Button>
                <Button size="sm" variant="outline" onClick={() => toast.info('Tasking distribusi ke K/L')} data-id="element-2598">
                  Distribusi ke K/L
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedEvent} onOpenChange={o => !o && setSelectedEvent(null)} data-id="element-2599">
        <DialogContent className="max-w-lg" data-id="element-2600">
          <DialogHeader data-id="element-2601">
            {selectedEvent && (() => {
            const meta = categoryMeta[selectedEvent.category];
            const d = new Date(selectedEvent.date);
            return <>
                    <div className="flex items-center gap-2 mb-1" data-id="element-2602">
                      <Badge variant="outline" className={`font-mono text-[9px] h-4 px-1.5 ${meta.bg} ${meta.text} ${meta.border}`} data-id="element-2603">
                        {meta.label}
                      </Badge>
                      <Badge variant="outline" className={`font-mono text-[9px] h-4 px-1.5 ${riskTone[selectedEvent.risk]}`} data-id="element-2604">
                        {selectedEvent.risk}
                      </Badge>
                      <span className="font-mono text-[10px] text-muted-foreground ml-auto" data-id="element-2605">
                        {d.getDate()} {months[d.getMonth()]} {d.getFullYear()}
                      </span>
                    </div>
                    <DialogTitle data-id="element-2606">{selectedEvent.title}</DialogTitle>
                    {selectedEvent.region && <DialogDescription data-id="element-2607">
                        📍 {selectedEvent.region}
                      </DialogDescription>}
                  </>;
          })()}
          </DialogHeader>
          {selectedEvent && <div className="space-y-3" data-id="element-2608">
              <div data-id="element-2609">
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-2610">
                  Prediksi Isu Terkait
                </p>
                <ul className="space-y-1.5" data-id="element-2611">
                  {selectedEvent.predictedIssues.map((iss: string, i: number) => <li key={i} className="flex items-start gap-2 text-xs" data-id="element-2612">
                        <span className="text-primary mt-1" data-id="element-2613">•</span>
                        <span className="leading-relaxed" data-id="element-2614">{iss}</span>
                      </li>)}
                </ul>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-id="element-2615">
                <p className="font-mono text-[10px] uppercase text-primary font-bold flex items-center gap-1.5" data-id="element-2616">
                  <AlertTriangle className="size-3" data-id="element-2617" /> Catatan AI Analyst
                </p>
                <p className="text-xs text-foreground/80 mt-1.5 leading-relaxed" data-id="element-2618">
                  Siapkan brief naratif terpadu 7 hari sebelum event ini.
                  Koordinasi K/L terkait dan monitoring lintas-platform
                  direkomendasikan.
                </p>
              </div>
            </div>}
          <DialogFooter data-id="element-2619">
            <Button variant="outline" onClick={() => setSelectedEvent(null)} data-id="element-2620">
              Tutup
            </Button>
            <Button onClick={() => {
            toast.success('Brief event dibuat oleh AI');
            setSelectedEvent(null);
          }} data-id="element-2621">
              Generate Brief
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}
function SummaryCard({
  tone,
  label,
  value,
  sub
}: {
  tone: 'primary' | 'red' | 'amber' | 'emerald';
  label: string;
  value: string;
  sub: string;
}) {
  const tones: Record<string, any> = {
    primary: 'from-primary/60 to-primary',
    red: 'from-red-400 to-red-600',
    amber: 'from-amber-400 to-amber-600',
    emerald: 'from-emerald-400 to-emerald-600'
  };
  return <Card className="bg-card border-border/60 overflow-hidden relative hover:shadow-md transition-all" data-id="element-2622">
      <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${tones[tone]}`} data-id="element-2623" />
      <CardContent className="p-3 sm:p-4 pl-4 sm:pl-5" data-id="element-2624">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-id="element-2625">
          {label}
        </p>
        <p className="font-heading text-2xl sm:text-3xl font-bold mt-1 tracking-tight" data-id="element-2626">
          {value}
        </p>
        <p className="font-mono text-[10px] text-muted-foreground mt-1" data-id="element-2627">
          {sub}
        </p>
      </CardContent>
    </Card>;
}